// Local evaluation accounts only. Client-side checks are not production security.
export const ACCOUNT_KEY = "praachin-admin-accounts-v1";
export const SESSION_KEY = "praachin-admin-account-session-v1";
const ITERATIONS = 210000,
  SESSION_MS = 12 * 60 * 60 * 1000;
const normalize = (email) =>
  String(email || "")
    .trim()
    .toLowerCase();
export function signupErrors({ name, email, password, confirmPassword }) {
  const e = {};
  if (!String(name || "").trim()) e.name = "Enter your full name.";
  else if (String(name).trim().length > 100)
    e.name = "Use 100 characters or fewer.";
  if (!normalize(email)) e.email = "Enter your email.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalize(email)))
    e.email = "Enter a valid email address.";
  if (!password) e.password = "Enter a password.";
  else if (
    password.length < 10 ||
    password.length > 128 ||
    !/[a-z]/.test(password) ||
    !/[A-Z]/.test(password) ||
    !/[0-9]/.test(password) ||
    !/[\W_]/.test(password)
  )
    e.password =
      "Use 10–128 characters, including uppercase, lowercase, a number and a symbol.";
  if (!confirmPassword) e.confirmPassword = "Confirm your password.";
  else if (password !== confirmPassword)
    e.confirmPassword = "Passwords must match.";
  return e;
}
export function loginErrors({ email, password }) {
  const e = {};
  if (!normalize(email)) e.email = "Enter your email.";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalize(email)))
    e.email = "Enter a valid email address.";
  if (!password) e.password = "Enter your password.";
  else if (password.length > 128) e.password = "Password is too long.";
  return e;
}
function cryptoApi() {
  const c = globalThis.crypto;
  if (!c?.subtle || !c.getRandomValues)
    throw Error(
      "Local accounts require a modern browser on localhost or HTTPS.",
    );
  return c;
}
const hex = (bytes) =>
  [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
const bytes = (s) =>
  new Uint8Array(s.match(/.{1,2}/g).map((h) => parseInt(h, 16)));
async function derive(password, salt, iterations) {
  const c = cryptoApi(),
    key = await c.subtle.importKey(
      "raw",
      new TextEncoder().encode(password),
      "PBKDF2",
      false,
      ["deriveBits"],
    );
  return hex(
    new Uint8Array(
      await c.subtle.deriveBits(
        { name: "PBKDF2", salt: bytes(salt), iterations, hash: "SHA-256" },
        key,
        256,
      ),
    ),
  );
}
export function accounts(storage) {
  let raw;
  try {
    raw = storage.getItem(ACCOUNT_KEY);
  } catch {
    throw Error("Account storage is unavailable in this browser.");
  }
  if (!raw) return { version: 1, accounts: [] };
  try {
    const d = JSON.parse(raw);
    if (d.version !== 1 || !Array.isArray(d.accounts)) throw Error();
    return d;
  } catch {
    throw Error(
      "Saved account data could not be read. No data was overwritten.",
    );
  }
}
export async function signup(storage, input) {
  const e = signupErrors(input);
  if (Object.keys(e).length)
    throw Object.assign(Error("Check the highlighted fields."), { fields: e });
  const email = normalize(input.email);
  if (accounts(storage).accounts.some((a) => a.email === email))
    throw Object.assign(
      Error("An account already exists with this email. Sign in instead."),
      { fields: { email: "This email is already registered." } },
    );
  const c = cryptoApi(),
    salt = hex(c.getRandomValues(new Uint8Array(16))),
    passwordHash = await derive(input.password, salt, ITERATIONS);
  const d = accounts(storage);
  if (d.accounts.some((a) => a.email === email))
    throw Object.assign(Error("This email is already registered."), {
      fields: { email: "This email is already registered." },
    });
  const account = {
    id: "acct-" + hex(c.getRandomValues(new Uint8Array(12))),
    name: String(input.name).trim(),
    email,
    role: "local-evaluator",
    enabled: true,
    createdAt: new Date().toISOString(),
    passwordHash,
    salt,
    iterations: ITERATIONS,
    algorithm: "PBKDF2-SHA-256",
  };
  d.accounts.push(account);
  try {
    storage.setItem(ACCOUNT_KEY, JSON.stringify(d));
  } catch {
    throw Error(
      "Account could not be saved. Browser storage may be full or unavailable.",
    );
  }
  return publicAccount(account);
}
export function publicAccount(a) {
  return a
    ? {
        id: a.id,
        name: a.name,
        email: a.email,
        role: a.role,
        createdAt: a.createdAt,
      }
    : null;
}
export async function login(storage, sessionStorage, input) {
  const e = loginErrors(input);
  if (Object.keys(e).length)
    throw Object.assign(Error("Check the highlighted fields."), { fields: e });
  const account = accounts(storage).accounts.find(
    (a) => a.email === normalize(input.email),
  );
  const salt = account?.salt || "00000000000000000000000000000000",
    iterations = account?.iterations || ITERATIONS;
  if (
    !/^[0-9a-f]{32}$/i.test(salt) ||
    !Number.isInteger(iterations) ||
    iterations < 100000 ||
    iterations > 1000000
  )
    throw Error("Saved account credentials are invalid.");
  const saved = account?.passwordHash || "".padStart(64, "0");
  if (!/^[0-9a-f]{64}$/i.test(saved))
    throw Error("Saved account credentials are invalid.");
  const hash = await derive(input.password, salt, iterations);
  let mismatch = 0;
  for (let i = 0; i < 64; i++)
    mismatch |= hash.charCodeAt(i) ^ saved.charCodeAt(i);
  if (
    !account ||
    mismatch ||
    !account.enabled ||
    account.role !== "local-evaluator"
  )
    throw Error("Email or password is incorrect.");
  const session = {
    accountId: account.id,
    issuedAt: Date.now(),
    expiresAt: Date.now() + SESSION_MS,
  };
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    throw Error(
      "Could not start the session. Browser session storage is unavailable.",
    );
  }
  return publicAccount(account);
}
export function getSession(storage, sessionStorage) {
  try {
    const session = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
    if (
      !session ||
      !Number.isFinite(session.expiresAt) ||
      session.expiresAt <= Date.now()
    )
      return null;
    const a = accounts(storage).accounts.find(
      (a) =>
        a.id === session.accountId && a.enabled && a.role === "local-evaluator",
    );
    return publicAccount(a);
  } catch {
    return null;
  }
}
export function logout(sessionStorage) {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem("praachin-admin-session");
  sessionStorage.removeItem("praachin-demo-session");
}
